import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertTriangle, Clock, Users, X } from "lucide-react";
import React, { useState } from "react";

interface MarkForDeletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  databaseName: string;
  databaseType: string;
}

const MarkForDeletionDialog: React.FC<MarkForDeletionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  databaseName,
  databaseType,
}) => {
  const [isMarking, setIsMarking] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsMarking(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsMarking(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return "🍃";
      default:
        return "🗄️";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <Card className="w-full max-w-lg max-h-[90vh]  mx-4 overflow-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <CardTitle className="text-lg">
                Mark Database for Deletion
              </CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isMarking}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            This will schedule the database for permanent deletion with a 7-day
            grace period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Database Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm font-medium text-blue-800 mb-1">
              Database to be marked for deletion:
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <span className="text-lg">{getDatabaseIcon(databaseType)}</span>
              <div>
                <div>
                  <strong>Name:</strong> {databaseName}
                </div>
                <div>
                  <strong>Type:</strong> {databaseType}
                </div>
              </div>
            </div>
          </div>

          {/* Immediate Effects */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-800">
                <div className="font-medium mb-1">
                  🚫 Immediate Effects (Right Now)
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    All database users will be{" "}
                    <strong>immediately disabled</strong>
                  </li>
                  <li>
                    Your applications will <strong>lose connection</strong> to
                    this database
                  </li>
                  <li>No new connections will be allowed</li>
                  <li>Existing connections will be terminated</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Grace Period Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <div className="font-medium mb-1">⏰ 7-Day Grace Period</div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Database will be <strong>permanently deleted</strong> in 7
                    calendar days
                  </li>
                  <li>
                    You can <strong>cancel the deletion</strong> anytime during
                    this period
                  </li>
                  <li>
                    If cancelled, users will be re-enabled and connections
                    restored
                  </li>
                  <li>After 7 days, deletion cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>

          {/* What happens to users */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                <div className="font-medium mb-1">👥 Database Users</div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    All users will be <strong>disabled immediately</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isMarking}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isMarking}
              className="flex-1"
            >
              {isMarking ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Marking for Deletion...
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Mark for Deletion
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarkForDeletionDialog;
